import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { stockInApi, vendorApi, warehouseApi, productsApi } from '../services/api';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import {
  Calendar, FileText, Plus,
  Boxes, Star, CircleDollarSign, Check, X,
  Save, Search, Download, CheckCircle2, AlertTriangle,
  ChevronRight, MapPin, ArrowRight, Trash2, Loader2, Hash
} from 'lucide-react';

const { RangePicker } = DatePicker;

const EMPTY_ROW = () => ({ productId: '', productName: '', category: '', batchNo: '', manufactureDate: '', expiryDate: '', purchaseRate: '', mrp: '', qty: '', bonusQty: '' });

function StockIn() {
  const { currentTheme } = useTheme();
  const isDark = ['Purple', 'Black'].includes(currentTheme?.name);

  // View state: 'list' (Stock In Entries dashboard) or 'form' (New Stock In form)
  const [viewMode, setViewMode] = useState('list');

  // Form Header States
  const [vendor, setVendor] = useState('');
  const [invoiceNo, setInvoiceNo] = useState('');
  const [poNo, setPoNo] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [warehouse, setWarehouse] = useState('');
  const [receiveDate, setReceiveDate] = useState('');
  const [remarks, setRemarks] = useState('');

  // Toast Notification State
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('New Stock added successfully!');

  // Attached Files State (abhi local — invoice upload endpoint aane pe wire karenge)
  const [uploadedFiles, setUploadedFiles] = useState([]);

  // Table Row State
  const [rows, setRows] = useState([
    { id: 1, ...EMPTY_ROW() },
    { id: 2, ...EMPTY_ROW() },
    { id: 3, ...EMPTY_ROW() }
  ]);

  // ── API DATA STATES ──
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null); // today/summary se totals
  const [vendorsList, setVendorsList] = useState([]);
  const [warehousesList, setWarehousesList] = useState([]);
  const [categoriesList] = useState([
    'Tablet',
    'Capsule',
    'Syrup',
    'Juice',
    'Ras',
    'Aswarista',
    'Vati',
    'Bhasma',
    'Guggul',
    'Cosmetic'
  ]);

  // ✅ NEW — Products master list (Stock-In form ka "Product Name" field ab isी se
  // searchable-select banega, free-text nahi. Jab tak koi row is list se ek product
  // select nahi karti, uska productId khaali rehta hai aur save par block ho jaata hai.)
  const [productsList, setProductsList] = useState([]);
  const [activeSuggestionRowId, setActiveSuggestionRowId] = useState(null);

  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);

  // Entries List Filter States
  const [listSearch, setListSearch] = useState('');
  const [listWarehouse, setListWarehouse] = useState('All');
  const [listSupplier, setListSupplier] = useState('All');
  const [listDateFrom, setListDateFrom] = useState('');
  const [listDateTo, setListDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  // Debounced search — har keystroke pe API call nahi
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(listSearch), 400);
    return () => clearTimeout(t);
  }, [listSearch]);

  // ── Backend entry → UI shape mapper ──
  // ⚠️ Field names 14_Updated_DTOs.java / Swagger se ek baar verify kar lena
 const initials = (name) => (name || '').split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase() || '—';
  const mapEntry = (e, idx) => ({
    id: e.id ?? idx,
    siNumber: e.siNumber || e.referenceNumber || `SI-${e.id}`,
    date: e.date || (e.createdAt ? new Date(e.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''),
    time: e.time || (e.createdAt ? new Date(e.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) : ''),
    vendor: e.vendorName || e.vendor || '',
    vendorInitials: initials(e.vendorName || e.vendor),
    warehouse: e.warehouseName || e.warehouse || '',
   products: (e.productsCount ?? e.totalItems) != null
  ? `${e.productsCount ?? e.totalItems} SKU${(e.productsCount ?? e.totalItems) !== 1 ? 's' : ''}`
  : (e.products || ''),

totalQty: (e.totalQty ?? e.totalQuantity) != null
  ? `+${Number(e.totalQty ?? e.totalQuantity).toLocaleString('en-IN')}`
  : '+0',
totalQtyRaw: Number(e.totalQty ?? e.totalQuantity) || 0,


    invoiceNo: e.invoiceNo || e.invoiceNumber || '',
  value: (e.totalValue ?? e.grandTotal) != null
  ? `₹${Number(e.totalValue ?? e.grandTotal).toLocaleString('en-IN')}`
  : '₹0',
valueRaw: Number(e.totalValue ?? e.grandTotal) || 0,
    status: e.status ? e.status.charAt(0) + e.status.slice(1).toLowerCase().replace('_', ' ') : 'Pending', // RECEIVED → Received
    createdBy: e.createdBy || e.createdByName || '',
    createdByInitials: initials(e.createdBy || e.createdByName),
  });

  // ── FETCH: Vendors + Warehouses + Products dropdowns (mount pe ek baar) ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [v, w, p] = await Promise.all([
          vendorApi.getActiveVendors(),
          warehouseApi.getActiveWarehouses(),
          productsApi.getAllProducts(),
        ]);
        if (cancelled) return;
        setVendorsList(Array.isArray(v) ? v : (v?.content || v?.data || []));
        setWarehousesList(Array.isArray(w) ? w : w?.centers || w?.content || []);
        const productArr = Array.isArray(p) ? p : (p?.content || p?.data || p?.products || []);
        setProductsList(Array.isArray(productArr) ? productArr : []);
        console.log('Dropdowns loaded:', { vendors: v, warehouses: w, products: p });
      } catch (err) {
        console.error('Dropdown load error:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── FETCH: Entries list (filters change pe server-side search) ──
  const loadEntries = async () => {
    setListLoading(true);
    setListError(null);
    try {
      const [res, sum] = await Promise.all([
      stockInApi.searchStockIn({
  searchTerm: debouncedSearch,
  warehouseId: listWarehouse,   // ab id hai
  vendorId: listSupplier,       // ab id hai
  status: statusFilter !== 'All' ? statusFilter.toUpperCase() : undefined,
  startDate: listDateFrom,
  endDate: listDateTo,
}),
        stockInApi.getTodaySummary().catch(() => null), // summary fail ho toh list mat rok
      ]);
      const list = Array.isArray(res) ? res : (res?.entries || res?.content || res?.data || []);
setEntries(Array.isArray(list) ? list.map(mapEntry) : []);
      if (sum) setSummary({
  receivedCount: res?.receivedCount,
  pendingPartialCount: (res?.pendingCount ?? 0) + (res?.partialCount ?? 0),
  totalUnits: res?.totalUnits,
  totalValue: res?.totalValue,
});

    } catch (err) {
      setListError(err.message || 'Failed to load stock-in entries');
      console.error('Entries load error:', err);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    loadEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, listWarehouse, listSupplier, statusFilter, listDateFrom, listDateTo]);

  // Add new product line row in form
  const addRow = () => {
    const nextId = rows.length > 0 ? Math.max(...rows.map(r => r.id)) + 1 : 1;
    setRows([...rows, { id: nextId, ...EMPTY_ROW() }]);
  };

  // Update a field inside a row in form
  const updateRow = (id, field, value) => {
    setRows(rows.map(row => row.id === id ? { ...row, [field]: value } : row));
  };

  // ✅ NEW — Product name typed manually → clear productId link until an actual
  // suggestion from the Products master list is picked. Prevents silently
  // submitting an unlinked/free-text product name.
  const updateProductNameInput = (id, value) => {
    setRows(rows.map(row => row.id === id ? { ...row, productName: value, productId: '' } : row));
    setActiveSuggestionRowId(id);
  };

  // ✅ NEW — User picks a product from the suggestion dropdown → link real productId
  const selectProductForRow = (rowId, product) => {
    setRows(rows.map(row => row.id === rowId
      ? { ...row, productName: product.productName || product.name || '', productId: product.id, category: product.category || '' }
      : row
    ));
    setActiveSuggestionRowId(null);
  };

  // ✅ NEW — Filter Products master list by typed text (case-insensitive, top 8)
  const getProductSuggestions = (searchTerm) => {
    if (!searchTerm || !searchTerm.trim()) return [];
    const term = searchTerm.toLowerCase();
    return productsList
      .filter(p => (p.productName || p.name || '').toLowerCase().includes(term))
      .slice(0, 8);
  };

  // File attach (UI only — invoice upload API backend me add hone pe FormData se bhejना)
  // eslint-disable-next-line no-unused-vars
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 5 * 1024 * 1024) {
        alert('File 5MB se badi hai');
        return;
      }
      const nextId = uploadedFiles.length > 0 ? Math.max(...uploadedFiles.map(f => f.id)) + 1 : 1;
      setUploadedFiles([...uploadedFiles, { id: nextId, name: file.name, status: 'Attached', file }]);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const removeFile = (id) => {
    setUploadedFiles(uploadedFiles.filter(f => f.id !== id));
  };

  // Calculate Sticky Footer Totals in form
  const totalItems = rows.filter(row => row.productName.trim() !== '').length;
  const totalQty = rows.reduce((sum, row) => sum + (parseInt(row.qty) || 0), 0);
  const totalBonus = rows.reduce((sum, row) => sum + (parseInt(row.bonusQty) || 0), 0);

  const grandTotal = rows.reduce((sum, row) => {
    const rate = parseFloat(row.purchaseRate) || 0;
    const quantity = parseInt(row.qty) || 0;
    return sum + (rate * quantity);
  }, 0);

  // Form payload builder (create + draft dono ke liye)
  const buildPayload = () => ({
    vendorId: vendor ? Number(vendor) : null,
    invoiceNo,
    poNumber: poNo,
    invoiceDate: invoiceDate || null,
    warehouseId: warehouse ? Number(warehouse) : null,
    receiveDate: receiveDate || null,
    remarks,
    items: rows
      .filter(r => r.productName.trim() !== '')
      .map(r => ({
        productId: r.productId ? Number(r.productId) : null,
        productName: r.productName,
        category: r.category || null,
        batchNo: r.batchNo,
        manufactureDate: r.manufactureDate || null,
        expiryDate: r.expiryDate || null,
        purchaseRate: parseFloat(r.purchaseRate) || 0,
        mrp: parseFloat(r.mrp) || 0,
        quantity: parseInt(r.qty) || 0,
        bonusQty: parseInt(r.bonusQty) || 0,
      })),
  });

  const resetForm = () => {
    setVendor('');
    setInvoiceNo('');
    setPoNo('');
    setInvoiceDate('');
    setWarehouse('');
    setReceiveDate('');
    setRemarks('');
    setUploadedFiles([]);
    setRows([
      { id: 1, ...EMPTY_ROW() },
      { id: 2, ...EMPTY_ROW() },
      { id: 3, ...EMPTY_ROW() }
    ]);
  };

  // ── Handle Save Stock In (API) ──
  const handleSaveStockIn = async () => {
    // Basic validation
    if (!vendor) return alert('Please select a vendor.');
    if (!invoiceNo) return alert('Please enter an invoice number.');
    if (!poNo) return alert('Please enter an Eway bill number.');
    if (!invoiceDate) return alert('Please select an invoice date.');
    if (!warehouse) return alert('Please select a destination warehouse.');
    if (totalItems === 0) return alert('Please add at least one product line.');
    if (totalQty === 0) return alert('Please enter quantity for at least one product.');

    // ✅ NEW — Har product line ek real productId se linked honi chahiye, warna
    // stock warehouse mein kabhi chadhega hi nahi (backend ko productId: null milta tha).
    const unlinkedRows = rows.filter(r => r.productName.trim() !== '' && !r.productId);
    if (unlinkedRows.length > 0) {
      return alert(
        `Ye products suggestion list se select nahi kiye gaye hain — pehle Products module mein banao, phir yahan list se select karo:\n${unlinkedRows.map(r => r.productName).join(', ')}`
      );
    }

    setSaving(true);
    try {
      await stockInApi.createStockIn(buildPayload());
      setToastMessage('New Stock added successfully!');
      setShowToast(true);
      setTimeout(() => {
        setShowToast(false);
        setViewMode('list');
        resetForm();
        loadEntries(); // list refresh
      }, 1500);
    } catch (err) {
      alert(err.message || 'Failed to save stock in');
    } finally {
      setSaving(false);
    }
  };

  // ── Handle Save as Draft (API) ──
  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      // Naya draft — id 'new' path use kar rahe; agar backend POST /stock-in with status=DRAFT expect karta hai
      // toh createStockIn({...payload, status: 'DRAFT'}) use karo. Swagger se confirm karna.
      await stockInApi.createStockIn({ ...buildPayload(), status: 'DRAFT' });
      setToastMessage('Draft saved successfully!');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2500);
    } catch (err) {
      alert(err.message || 'Failed to save draft');
    } finally {
      setSavingDraft(false);
    }
  };

  // Form Reset / Discard
  const handleDiscard = () => {
    if (window.confirm("Are you sure you want to discard this draft?")) {
      resetForm();
      setViewMode('list');
    }
  };

  // ── PDF/Excel export (API) — backend endpoints ready:
  // GET /api/stock-in/export/pdf and /api/stock-in/export/excel,
  // dono active search/date filters ke saath filtered report dete hain.
  const handleExport = async (format) => {
    try {
      const params = {
        search: debouncedSearch || undefined,
        startDate: listDateFrom || undefined,
        endDate: listDateTo || undefined,
      };

      const blob = format === 'pdf'
        ? await stockInApi.exportStockInPdf(params)
        : await stockInApi.exportStockInExcel(params);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `stock-in-report.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      alert(err.message || `Failed to export ${format}`);
    }
  };

  // Entries ab server-side filtered hain — direct use
  const filteredEntries = entries;

  // Metrics — summary API se, fallback current list pe
  const totalReceivedCount = summary?.receivedCount ?? entries.filter(e => e.status === 'Received').length;
  const totalPendingPartialCount = summary?.pendingPartialCount ?? entries.filter(e => ['Pending', 'Partial'].includes(e.status)).length;
  const totalUnits = summary?.totalUnits ?? entries.reduce((sum, e) => sum + e.totalQtyRaw, 0);
  const totalValue = summary?.totalValue ?? entries.reduce((sum, e) => sum + e.valueRaw, 0);

  // Status Filter Counts helper (current list pe)
  const getCount = (status) => entries.filter(e => e.status === status).length;

  return (
    <div className="space-y-6 pb-28 relative">
      {/* ── SUCCESS TOAST POPUP ── */}
      {showToast && (
        <div className="fixed top-24 left-1/2 transform -translate-x-1/2 z-[10000] transition-all duration-300">
          <div className="flex items-center gap-3 bg-[#eefdf5] border border-emerald-500/30 px-4 py-3 rounded-xl shadow-lg">
            <div className="w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
              <Check className="w-3.5 h-3.5 stroke-[3]" />
            </div>
            <span className="text-sm font-bold text-gray-800">
              {toastMessage}
            </span>
            <button
              onClick={() => setShowToast(false)}
              className="w-5 h-5 rounded-full border border-rose-200 text-rose-500 flex items-center justify-center hover:bg-rose-50 transition-all ml-4 cursor-pointer"
            >
              <X className="w-3.5 h-3.5 stroke-[3]" />
            </button>
          </div>
        </div>
      )}

      {/* Render conditional view mode */}
      {viewMode === 'list' ? (
        <>
          {/* ── HEADER ── */}
          <div className="flex justify-between items-center flex-wrap gap-4">
            <div>
              <h1 className={`text-2xl desktop:text-3xl font-black tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Stock In Entries
              </h1>
              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-550'} mt-1`}>
                {listLoading ? 'Loading...' : `${filteredEntries.length} total entries · All warehouses`}
              </p>
            </div>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              {/* General Search Input */}
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search SI number, vendor, invoice..."
                  value={listSearch}
                  onChange={(e) => setListSearch(e.target.value)}
                  className={`pl-10 pr-4 py-2 rounded-xl text-xs w-full outline-none border focus:border-emerald-500 transition-all ${
                    isDark
                      ? 'bg-slate-900 border-slate-800 text-white placeholder-slate-500'
                      : 'bg-white border-gray-200 text-gray-800 placeholder-gray-400 shadow-sm'
                  }`}
                />
              </div>

              {/* PDF */}
              <button
                onClick={() => handleExport('pdf')}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl border text-xs font-bold hover:opacity-85 transition-all cursor-pointer ${
                  isDark ? 'bg-slate-900 border-slate-800 text-white' : 'bg-white border-gray-200 text-gray-700 shadow-sm'
                }`}
              >
                <FileText className="w-3.5 h-3.5 text-rose-500" />
                PDF
              </button>

              {/* Excel */}
              <button
                onClick={() => handleExport('excel')}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl border text-xs font-bold hover:opacity-85 transition-all cursor-pointer ${
                  isDark ? 'bg-slate-900 border-slate-800 text-white' : 'bg-white border-gray-200 text-gray-700 shadow-sm'
                }`}
              >
                <Download className="w-3.5 h-3.5 text-emerald-600" />
                Export Excel
              </button>

              {/* New Stock In trigger */}
              <button
                onClick={() => setViewMode('form')}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-white hover:opacity-90 transition-all shadow-md cursor-pointer whitespace-nowrap"
                style={{ backgroundColor: currentTheme.color }}
              >
                <Plus className="w-3.5 h-3.5" />
                New Stock In
              </button>
            </div>
          </div>

          {/* ── ERROR BANNER ── */}
          {listError && (
            <div className="flex items-center gap-3 p-4 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 text-sm font-semibold">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span>{listError}</span>
              <button onClick={loadEntries} className="ml-auto text-xs font-black uppercase tracking-wider underline cursor-pointer">
                Retry
              </button>
            </div>
          )}

          {/* ── METRICS GRID & STATUS FILTERS ROW ── */}
          <div className="space-y-4">
            {/* Row 1: Metrics Cards (5 columns - single line) */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">

              {/* Card 1: Total Entries */}
              <div className={`p-3.5 rounded-xl border flex items-center gap-3 ${
                isDark ? 'bg-slate-900/40 border-slate-800/80 shadow-2xl' : 'bg-white border-gray-150 shadow-sm'
              }`}>
                <div className="w-8 h-8 rounded-lg bg-slate-900 text-white flex items-center justify-center flex-shrink-0 shadow-sm">
                  <FileText className="w-4 h-4" />
                </div>
                <div>
                  <p className={`text-lg font-black leading-none ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {listLoading ? '—' : entries.length}
                  </p>
                  <p className="text-[9px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wider mt-1 mb-0.5">Total Entries</p>
                  <p className="text-[8px] text-gray-500 dark:text-slate-400 font-medium leading-none">in view</p>
                </div>
              </div>

              {/* Card 2: Total Units */}
              <div className={`p-3.5 rounded-xl border flex items-center gap-3 ${
                isDark ? 'bg-slate-900/40 border-slate-800/80 shadow-2xl' : 'bg-white border-gray-150 shadow-sm'
              }`}>
                <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-950/20 dark:text-blue-400 flex items-center justify-center flex-shrink-0">
                  <Boxes className="w-4 h-4" />
                </div>
                <div>
                  <p className={`text-lg font-black leading-none ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {listLoading ? '—' : Number(totalUnits).toLocaleString('en-IN')}
                  </p>
                  <p className="text-[9px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wider mt-1 mb-0.5">Total Units</p>
                  <p className="text-[8px] text-gray-500 dark:text-slate-400 font-medium leading-none">units received</p>
                </div>
              </div>

              {/* Card 3: Total Value */}
              <div className={`p-3.5 rounded-xl border flex items-center gap-3 ${
                isDark ? 'bg-slate-900/40 border-slate-800/80 shadow-2xl' : 'bg-white border-gray-150 shadow-sm'
              }`}>
                <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-655 dark:bg-purple-950/20 dark:text-purple-400 flex items-center justify-center flex-shrink-0">
                  <CircleDollarSign className="w-4 h-4" />
                </div>
                <div>
                  <p className={`text-lg font-black leading-none ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {listLoading ? '—' : `₹${Math.round(Number(totalValue) / 1000).toLocaleString('en-IN')}K`}
                  </p>
                  <p className="text-[9px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wider mt-1 mb-0.5">Total Value</p>
                  <p className="text-[8px] text-gray-500 dark:text-slate-400 font-medium leading-none">purchase value</p>
                </div>
              </div>

              {/* Card 4: Received */}
              <div className={`p-3.5 rounded-xl border flex items-center gap-3 ${
                isDark ? 'bg-slate-900/40 border-slate-800/80 shadow-2xl' : 'bg-white border-gray-150 shadow-sm'
              }`}>
                <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 dark:bg-emerald-950/20 dark:text-emerald-450 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-4 h-4" />
                </div>
                <div>
                  <p className={`text-lg font-black leading-none ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {listLoading ? '—' : totalReceivedCount}
                  </p>
                  <p className="text-[9px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wider mt-1 mb-0.5">Received</p>
                  <p className="text-[8px] text-gray-500 dark:text-slate-400 font-medium leading-none">completed</p>
                </div>
              </div>

              {/* Card 5: Pending/Partial */}
              <div className={`p-3.5 rounded-xl border flex items-center gap-3 ${
                isDark ? 'bg-slate-900/40 border-slate-800/80 shadow-2xl' : 'bg-white border-gray-150 shadow-sm'
              }`}>
                <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 dark:bg-amber-955/20 dark:text-amber-400 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-4 h-4" />
                </div>
                <div>
                  <p className={`text-lg font-black leading-none ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {listLoading ? '—' : totalPendingPartialCount}
                  </p>
                  <p className="text-[9px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wider mt-1 mb-0.5">Pending / Partial</p>
                  <p className="text-[8px] text-gray-500 dark:text-slate-400 font-medium leading-none">need action</p>
                </div>
              </div>

            </div>

            {/* Row 2: Quick Status Filters Pills Row */}
            <div className="flex flex-wrap items-center gap-2.5 pt-2">
              {['All', 'Received', 'Pending', 'Partial', 'Cancelled'].map(status => {
                const isActive = statusFilter === status;
                const dotColor = {
                  Received: 'bg-emerald-500',
                  Pending: 'bg-amber-500',
                  Partial: 'bg-blue-500',
                  Cancelled: 'bg-rose-500',
                }[status];
                return (
                  <button
                    key={status}
                    onClick={() => setStatusFilter(isActive && status !== 'All' ? 'All' : status)}
                    style={isActive ? { backgroundColor: currentTheme.color, borderColor: currentTheme.color } : {}}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-bold transition-all cursor-pointer ${
                      isActive
                        ? 'text-white shadow-sm font-black'
                        : isDark ? 'bg-slate-900/40 border-slate-800 text-gray-450 hover:text-white' : 'bg-white border-gray-150 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {dotColor && <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-white' : dotColor}`} />}
                    {status} ({status === 'All' ? entries.length : getCount(status)})
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── FILTERS PANEL CARD ── */}
          <div className={`p-4 rounded-xl border flex flex-wrap items-center gap-4 ${
            isDark ? 'bg-slate-900/60 border-slate-800' : 'bg-white border-gray-200 shadow-sm'
          }`}>
            {/* Search Input */}
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search ID, vendor, invoice..."
                value={listSearch}
                onChange={(e) => setListSearch(e.target.value)}
                className={`pl-9 pr-4 py-2 w-full rounded-xl border text-xs outline-none focus:border-emerald-500 transition-all ${
                  isDark ? 'bg-slate-950 border-slate-800 text-white' : 'bg-white border-gray-200 text-gray-750'
                }`}
              />
            </div>

            {/* Warehouse Select — API se */}
            <select
              value={listWarehouse}
              onChange={(e) => setListWarehouse(e.target.value)}
              className={`px-3 py-2 rounded-xl border text-xs outline-none focus:border-emerald-500 transition-all ${
                isDark ? 'bg-slate-955 border-slate-800 text-white' : 'bg-white border-gray-200 text-gray-750'
              }`}
            >
             <option value="All">All Warehouses</option>
{warehousesList.map(wh => (
  <option key={wh.id} value={wh.id}>{wh.name}</option>
))}
            </select>

            {/* Supplier Select — API se */}
            <select
              value={listSupplier}
              onChange={(e) => setListSupplier(e.target.value)}
              className={`px-3 py-2 rounded-xl border text-xs outline-none focus:border-emerald-500 transition-all ${
                isDark ? 'bg-slate-955 border-slate-800 text-white' : 'bg-white border-gray-200 text-gray-750'
              }`}
            >
            <option value="All">All Supplier</option>
{vendorsList.map(v => (
  <option key={v.id} value={v.id}>{v.name || v.companyName}</option>
))}

            </select>

            {/* Dates pickers */}
            <div className="flex items-center">
              <RangePicker
                value={
                  listDateFrom && listDateTo
                    ? [dayjs(listDateFrom), dayjs(listDateTo)]
                    : null
                }
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    setListDateFrom(dates[0].format('YYYY-MM-DD'));
                    setListDateTo(dates[1].format('YYYY-MM-DD'));
                  } else {
                    setListDateFrom('');
                    setListDateTo('');
                  }
                }}
                format="DD-MM-YYYY"
                style={{ borderRadius: '12px', padding: '6px 12px' }}
                className="text-xs"
                size="middle"
              />
            </div>
          </div>

          {/* ── MAIN ENTRIES TABLE CARD ── */}
          <div className={`border rounded-2xl p-6 transition-all ${
            isDark ? 'bg-slate-900/40 border-slate-800/80 shadow-2xl' : 'bg-white border-gray-150 shadow-sm'
          }`}>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-slate-800/50 text-[10px] font-black uppercase tracking-wider text-emerald-800 dark:text-emerald-500">
                    <th className="pb-3 pl-2">SI Number</th>
                    <th className="pb-3">Date</th>
                    <th className="pb-3">Vendor</th>
                    <th className="pb-3">Warehouse</th>
                    <th className="pb-3">Products</th>
                    <th className="pb-3 text-center">Total Qty</th>
                    <th className="pb-3">Invoice No</th>
                    <th className="pb-3">Value</th>
                    <th className="pb-3">Status</th>
                    <th className="pb-3 pr-2 text-center">Created By</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-slate-800/40 text-xs">
                  {listLoading ? (
                    <tr>
                      <td colSpan="10" className="py-12 text-center">
                        <Loader2 className="w-5 h-5 animate-spin text-emerald-500 mx-auto mb-2" />
                        <span className="text-gray-400 text-sm">Loading entries...</span>
                      </td>
                    </tr>
                  ) : filteredEntries.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="py-12 text-center text-gray-400 text-sm font-semibold">
                        No stock-in entries found matching the active filters.
                      </td>
                    </tr>
                  ) : (
                    filteredEntries.map(e => (
                      <tr key={e.id} className="hover:bg-slate-50/20 dark:hover:bg-slate-900/5 transition-colors">
                        {/* SI No */}
                        <td className="py-4 pl-2 font-black text-emerald-600 dark:text-emerald-400">
                          {e.siNumber}
                        </td>

                        {/* Date & Time */}
                        <td className="py-4">
                          <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{e.date}</p>
                          <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-0.5">{e.time}</p>
                        </td>

                        {/* Vendor */}
                        <td className="py-4">
                          <div className="flex items-center gap-2">
                            <span className="w-6 h-6 rounded-full bg-emerald-700 text-white flex items-center justify-center font-black text-[9px]">
                              {e.vendorInitials}
                            </span>
                            <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{e.vendor}</span>
                          </div>
                        </td>

                        {/* Warehouse */}
                        <td className="py-4">
                          <div className="flex items-center gap-1 text-gray-500 dark:text-slate-400">
                            <MapPin className="w-3.5 h-3.5 text-gray-400" />
                            <span className="font-medium">{e.warehouse}</span>
                          </div>
                        </td>

                        {/* Products Count */}
                        <td className="py-4 font-bold text-gray-600 dark:text-slate-300">
                          {e.products}
                        </td>

                        {/* Total Qty */}
                        <td className="py-4 text-center font-black text-emerald-600 dark:text-emerald-400 text-sm">
                          {e.totalQty}
                        </td>

                        {/* Invoice No */}
                        <td className="py-4 text-[11px] font-mono text-gray-500 dark:text-slate-450 uppercase tracking-tight">
                          {e.invoiceNo}
                        </td>

                        {/* Value */}
                        <td className="py-4 font-black text-gray-800 dark:text-slate-200">
                          {e.value}
                        </td>

                        {/* Status badge */}
                        <td className="py-4">
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
                            e.status === 'Received'
                              ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/20 dark:text-emerald-400'
                              : e.status === 'Pending'
                                ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/20 dark:text-amber-400'
                                : e.status === 'Partial'
                                  ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/20 dark:text-blue-400'
                                  : 'bg-rose-50 text-rose-700 dark:bg-rose-950/20 dark:text-rose-400'
                          }`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${
                              e.status === 'Received' ? 'bg-emerald-500' : e.status === 'Pending' ? 'bg-amber-500' : e.status === 'Partial' ? 'bg-blue-500' : 'bg-rose-500'
                            }`} />
                            {e.status}
                          </span>
                        </td>

                        {/* Created By user profile */}
                        <td className="py-4 pr-2">
                          <div className="flex items-center justify-between gap-1">
                            <div className="flex items-center gap-2">
                              <span className="w-6 h-6 rounded-full bg-slate-700 text-white flex items-center justify-center font-black text-[9px]">
                                {e.createdByInitials}
                              </span>
                              <span className={`font-medium ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>{e.createdBy}</span>
                            </div>
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                          </div>
                        </td>

                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Bottom count label */}
            <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-100 dark:border-slate-800/40 text-xs font-bold text-gray-400">
              <span>Showing {filteredEntries.length} entries</span>
              <span className="flex items-center gap-6">
                <span>Total: <strong className="text-gray-700 dark:text-white">{Number(totalUnits).toLocaleString('en-IN')} units</strong></span>
                <span>Value: <strong className="text-gray-700 dark:text-white">₹{Number(totalValue).toLocaleString('en-IN')}</strong></span>
              </span>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* ── NEW STOCK IN FORM VIEW ── */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className={`text-2xl desktop:text-3xl font-black tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                New Stock In
              </h1>
              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-550'} mt-1`}>
                Record incoming stock from vendor
              </p>
            </div>

            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-1 px-4 py-2 border rounded-xl text-xs font-bold hover:bg-gray-50 dark:hover:bg-slate-800 transition-all cursor-pointer ${
                isDark ? 'border-slate-800 text-gray-300' : 'border-gray-200 text-gray-600'
              }`}
            >
              <ArrowRight className="w-3.5 h-3.5 rotate-180" />
              Back to List
            </button>
          </div>

          {/* ── CARD 1: PURCHASE DETAILS ── */}
          <div className={`border rounded-2xl p-6 transition-all ${
            isDark ? 'bg-slate-900/40 border-slate-800/80 shadow-2xl' : 'bg-white border-gray-150 shadow-sm'
          }`}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full flex items-center justify-center text-emerald-600 bg-emerald-50 dark:bg-emerald-950/20">
                <FileText className="w-5 h-5" />
              </div>
              <div>
                <h2 className={`text-sm font-black uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  Purchase Details
                </h2>
                <p className={`text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-500'} font-medium`}>
                  Vendor and invoice information
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase tracking-wider text-gray-400 dark:text-slate-500">
                  Vendor / Supplier <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  {/* value = vendorId (payload me id jaata hai) */}
                  <select
                    value={vendor}
                    onChange={(e) => setVendor(e.target.value)}
                    className={`pl-9 pr-3 py-2.5 rounded-xl border text-sm w-full outline-none focus:border-emerald-500 transition-all ${
                      isDark ? 'bg-slate-955/40 border-slate-800 text-white' : 'bg-white border-gray-200 text-gray-800'
                    }`}
                  >
                    <option value="">Select vendor...</option>
                    {vendorsList.map(v => (
                      <option key={v.id} value={v.id}>{v.name || v.companyName}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase tracking-wider text-gray-400 dark:text-slate-500">
                  Invoice Number <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="e.g. INV-2024-00891"
                    value={invoiceNo}
                    onChange={(e) => setInvoiceNo(e.target.value)}
                    className={`pl-9 pr-3 py-2.5 rounded-xl border text-sm w-full outline-none focus:border-emerald-500 transition-all ${
                      isDark ? 'bg-slate-955/40 border-slate-800 text-white' : 'bg-white border-gray-200 text-gray-800'
                    }`}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase tracking-wider text-gray-400 dark:text-slate-500">
                  Eway Bill Number <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="e.g. PO-2024-00..."
                    value={poNo}
                    onChange={(e) => setPoNo(e.target.value)}
                    className={`pl-9 pr-3 py-2.5 rounded-xl border text-sm w-full outline-none focus:border-emerald-500 transition-all ${
                      isDark ? 'bg-slate-955/40 border-slate-800 text-white' : 'bg-white border-gray-200 text-gray-800'
                    }`}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase tracking-wider text-gray-400 dark:text-slate-500">
                  Invoice Date <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <input
                    type="date"
                    value={invoiceDate}
                    onChange={(e) => setInvoiceDate(e.target.value)}
                    className={`pl-9 pr-10 py-2.5 rounded-xl border text-sm w-full outline-none focus:border-emerald-500 transition-all ${
                      isDark ? 'bg-slate-955/40 border-slate-800 text-white' : 'bg-white border-gray-200 text-gray-800'
                    }`}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mt-5">
              <div className="space-y-1 md:col-span-2">
                <label className="text-[10px] font-black uppercase tracking-wider text-gray-400 dark:text-slate-500">
                  Receiving Warehouse <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  {/* value = warehouseId */}
                  <select
                    value={warehouse}
                    onChange={(e) => setWarehouse(e.target.value)}
                    className={`pl-9 pr-3 py-2.5 rounded-xl border text-sm w-full outline-none focus:border-emerald-500 transition-all ${
                      isDark ? 'bg-slate-955/40 border-slate-800 text-white' : 'bg-white border-gray-200 text-gray-800'
                    }`}
                  >
                    <option value="">Select warehouse...</option>
                    {warehousesList.map(wh => (
                      <option key={wh.id} value={wh.id}>
                        {wh.name || wh.name || wh.code}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase tracking-wider text-gray-400 dark:text-slate-500">
                  Receive Date
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <input
                    type="date"
                    value={receiveDate}
                    onChange={(e) => setReceiveDate(e.target.value)}
                    className={`pl-9 pr-10 py-2.5 rounded-xl border text-sm w-full outline-none focus:border-emerald-500 transition-all ${
                      isDark ? 'bg-slate-955/40 border-slate-800 text-white' : 'bg-white border-gray-200 text-gray-800'
                    }`}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase tracking-wider text-gray-400 dark:text-slate-500">
                  Remarks
                </label>
                <div className="relative">
                  <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Optional note about this receipt..."
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    className={`pl-9 pr-3 py-2.5 rounded-xl border text-sm w-full outline-none focus:border-emerald-500 transition-all ${
                      isDark ? 'bg-slate-955/40 border-slate-800 text-white' : 'bg-white border-gray-200 text-gray-800'
                    }`}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ── CARD 2: PRODUCT LIST TABLE ── */}
          <div className={`border rounded-2xl p-6 transition-all ${
            isDark ? 'bg-slate-900/40 border-slate-800/80 shadow-2xl' : 'bg-white border-gray-150 shadow-sm'
          }`}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full flex items-center justify-center text-emerald-600 bg-emerald-50 dark:bg-emerald-950/20">
                <Boxes className="w-5 h-5" />
              </div>
              <div>
                <h2 className={`text-sm font-black uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  Product Lines
                </h2>
                <p className={`text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-500'} font-medium`}>
                  {totalItems} products · {totalQty} units + {totalBonus} bonus
                </p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse min-w-[1300px]">
                <thead>
                  <tr className={`border-b text-[10px] font-black uppercase tracking-wider text-[#5c8a36] ${
                    isDark ? 'border-slate-800' : 'border-gray-150'
                  }`}>
                    <th className="pb-3 w-10 text-center">#</th>
                    <th className="pb-3 pl-2 min-w-[180px]">PRODUCT NAME</th>
                    <th className="pb-3 w-36">CATEGORY</th>
                    <th className="pb-3 w-32">BATCH NO.</th>
                    <th className="pb-3 w-40">MANUFACTURE DATE</th>
                    <th className="pb-3 w-40">EXPIRY DATE</th>
                    <th className="pb-3 w-32 text-center">PURCHASE RATE</th>
                    <th className="pb-3 w-32 text-center">MRP (₹)</th>
                    <th className="pb-3 w-24 text-center">QUANTITY</th>
                    <th className="pb-3 w-24 text-center">BONUS QTY</th>
                    <th className="pb-3 w-24 text-center">TOTAL QTY</th>
                    <th className="pb-3 w-20"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-slate-800/40">
                  {rows.map((row, idx) => {
                    const totalRowQty = (parseInt(row.qty) || 0) + (parseInt(row.bonusQty) || 0);
                    return (
                      <tr key={row.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-900/10">
                        {/* Index */}
                        <td className={`py-4 text-center text-xs font-black ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                          {idx + 1}
                        </td>

                        {/* Product Name — searchable autocomplete, must resolve to a real productId
                            from the Products master list. Free text with no match cannot be saved. */}
                        <td className="py-4 pr-3 pl-2 relative">
                          <div className="relative">
                            <input
                              type="text"
                              placeholder="Search product..."
                              value={row.productName}
                              onChange={(e) => updateProductNameInput(row.id, e.target.value)}
                              onFocus={() => setActiveSuggestionRowId(row.id)}
                              onBlur={() => setTimeout(() => {
                                setActiveSuggestionRowId(prev => (prev === row.id ? null : prev));
                              }, 150)}
                              className={`px-4 py-2 pr-8 rounded-full border text-xs w-full outline-none focus:border-emerald-500 transition-all ${
                                row.productName.trim() !== '' && !row.productId
                                  ? 'border-amber-400 focus:border-amber-500'
                                  : isDark ? 'border-slate-800' : 'border-gray-200'
                              } ${
                                isDark
                                  ? 'bg-slate-900 text-white placeholder-slate-500'
                                  : 'bg-white text-gray-800 placeholder-gray-400'
                              }`}
                            />
                            {row.productId && (
                              <Check className="w-3.5 h-3.5 text-emerald-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                            )}
                          </div>

                          {row.productName.trim() !== '' && !row.productId && (
                            <p className="text-[9px] font-bold text-amber-500 mt-1 pl-1 whitespace-nowrap">
                              ⚠ List se select karo — naya product Products module mein banao
                            </p>
                          )}

                          {activeSuggestionRowId === row.id && (
                            <div className={`absolute z-20 mt-1 w-64 max-h-52 overflow-y-auto rounded-xl border shadow-lg ${
                              isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'
                            }`}>
                              {getProductSuggestions(row.productName).length > 0 ? (
                                getProductSuggestions(row.productName).map(p => (
                                  <button
                                    type="button"
                                    key={p.id}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => selectProductForRow(row.id, p)}
                                    className={`w-full text-left px-3 py-2 text-xs font-semibold transition-colors cursor-pointer ${
                                      isDark ? 'text-gray-200 hover:bg-slate-800' : 'text-gray-700 hover:bg-gray-50'
                                    }`}
                                  >
                                    {p.productName || p.name}
                                    {p.sku && <span className="text-[9px] text-gray-400 ml-1.5 font-mono">({p.sku})</span>}
                                  </button>
                                ))
                              ) : row.productName.trim() !== '' ? (
                                <p className="px-3 py-2 text-[10px] text-gray-400 font-medium">
                                  Koi match nahi mila — Products module mein pehle create karo.
                                </p>
                              ) : (
                                <p className="px-3 py-2 text-[10px] text-gray-400 font-medium">
                                  Type karke product search karo...
                                </p>
                              )}
                            </div>
                          )}
                        </td>

                        {/* Category */}
                        <td className="py-4 pr-3">
                          <select
                            value={row.category}
                            onChange={(e) => updateRow(row.id, 'category', e.target.value)}
                            className={`px-4 py-2 rounded-full border text-xs w-full outline-none focus:border-emerald-500 transition-all ${
                              isDark
                                ? 'bg-slate-900 border-slate-800 text-white'
                                : 'bg-white border-gray-200 text-gray-800'
                            }`}
                          >
                            <option value="">Please select</option>
                            {categoriesList.map((cat, idx) => (
                              <option key={idx} value={cat}>{cat}</option>
                            ))}
                          </select>
                        </td>

                        {/* Batch No */}
                        <td className="py-4 pr-3">
                          <input
                            type="text"
                            placeholder="Batch..."
                            value={row.batchNo}
                            onChange={(e) => updateRow(row.id, 'batchNo', e.target.value)}
                            className={`px-4 py-2 rounded-full border text-xs w-full outline-none text-center focus:border-emerald-500 transition-all ${
                              isDark
                                ? 'bg-slate-900 border-slate-800 text-white placeholder-slate-500'
                                : 'bg-white border-gray-200 text-gray-800 placeholder-gray-400'
                            }`}
                          />
                        </td>

                        {/* Manufacture Date */}
                        <td className="py-4 pr-3">
                          <div className="relative w-full">
                            <input
                              type="date"
                              value={row.manufactureDate}
                              onChange={(e) => updateRow(row.id, 'manufactureDate', e.target.value)}
                              className={`pl-4 pr-8 py-2 rounded-full border text-xs w-full outline-none focus:border-emerald-500 transition-all appearance-none ${
                                isDark
                                  ? 'bg-slate-900 border-slate-800 text-white'
                                  : 'bg-white border-gray-200 text-gray-800'
                              }`}
                            />
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-emerald-600">
                              <Calendar className="w-3.5 h-3.5" />
                            </div>
                          </div>
                        </td>

                        {/* Expiry Date */}
                        <td className="py-4 pr-3">
                          <div className="relative w-full">
                            <input
                              type="date"
                              value={row.expiryDate}
                              onChange={(e) => updateRow(row.id, 'expiryDate', e.target.value)}
                              className={`pl-4 pr-8 py-2 rounded-full border text-xs w-full outline-none focus:border-emerald-500 transition-all appearance-none ${
                                isDark
                                  ? 'bg-slate-900 border-slate-800 text-white'
                                  : 'bg-white border-gray-200 text-gray-800'
                              }`}
                            />
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-emerald-600">
                              <Calendar className="w-3.5 h-3.5" />
                            </div>
                          </div>
                        </td>

                        {/* Purchase Rate */}
                        <td className="py-4 pr-3">
                          <div className="relative rounded-full">
                            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                              <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>₹</span>
                            </div>
                            <input
                              type="number"
                              placeholder="0.00"
                              value={row.purchaseRate}
                              onChange={(e) => updateRow(row.id, 'purchaseRate', e.target.value)}
                              className={`pl-7 pr-3 py-2 rounded-full border text-xs w-full text-center outline-none focus:border-emerald-500 transition-all ${
                                isDark
                                  ? 'bg-slate-900 border-slate-800 text-white'
                                  : 'bg-white border-gray-200 text-gray-850'
                              }`}
                            />
                          </div>
                        </td>

                        {/* MRP */}
                        <td className="py-4 pr-3">
                          <div className="relative rounded-full">
                            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                              <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>₹</span>
                            </div>
                            <input
                              type="number"
                              placeholder="0.00"
                              value={row.mrp}
                              onChange={(e) => updateRow(row.id, 'mrp', e.target.value)}
                              className={`pl-7 pr-3 py-2 rounded-full border text-xs w-full text-center outline-none focus:border-emerald-500 transition-all ${
                                isDark
                                  ? 'bg-slate-900 border-slate-800 text-white'
                                  : 'bg-white border-gray-200 text-gray-850'
                              }`}
                            />
                          </div>
                        </td>

                        {/* Quantity */}
                        <td className="py-4 pr-3">
                          <input
                            type="number"
                            placeholder="0"
                            value={row.qty}
                            onChange={(e) => updateRow(row.id, 'qty', e.target.value)}
                            className={`px-3 py-2 rounded-full border text-xs w-full text-center outline-none focus:border-emerald-500 transition-all ${
                              isDark
                                ? 'bg-slate-900 border-slate-800 text-white'
                                : 'bg-white border-gray-200 text-gray-850'
                            }`}
                          />
                        </td>

                        {/* Bonus Qty */}
                        <td className="py-4 pr-3">
                          <input
                            type="number"
                            placeholder="0"
                            value={row.bonusQty}
                            onChange={(e) => updateRow(row.id, 'bonusQty', e.target.value)}
                            className={`px-3 py-2 rounded-full border text-xs w-full text-center outline-none focus:border-emerald-500 transition-all ${
                              isDark
                                ? 'bg-emerald-955/20 border-emerald-900/30 text-emerald-400'
                                : 'bg-[#eefdf5] border-emerald-100 text-emerald-700'
                            }`}
                          />
                        </td>

                        {/* Total Qty (Disabled/Read-only Tag style) */}
                        <td className="py-4 pr-3 text-center">
                          <span className={`inline-flex items-center px-4 py-2 rounded-full text-xs font-black w-full justify-center ${
                            isDark ? 'bg-slate-800 text-slate-400' : 'bg-gray-100 text-gray-550'
                          }`}>
                            {totalRowQty > 0 ? totalRowQty : '-'}
                          </span>
                        </td>

                        {/* Actions (Clear & Delete) */}
                        <td className="py-4 text-center">
                          <div className="flex items-center gap-2.5 justify-center">
                            {/* Clear/Reset button */}
                            <button
                              onClick={() => {
                                setRows(rows.map(r => r.id === row.id ? { id: r.id, ...EMPTY_ROW() } : r));
                              }}
                              className="text-amber-500 hover:text-amber-600 transition-colors p-1"
                              title="Clear row"
                            >
                              <X className="w-4 h-4" />
                            </button>
                            {/* Delete button */}
                            <button
                              onClick={() => {
                                if (rows.length > 1) {
                                  setRows(rows.filter(r => r.id !== row.id));
                                } else {
                                  alert("At least one product line is required.");
                                }
                              }}
                              className="text-rose-500 hover:text-rose-600 transition-colors p-1"
                              title="Delete row"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Add Another Row Button */}
            <button
              onClick={addRow}
              className="mt-4 inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-wider text-[#0f975d] hover:text-emerald-700 transition-colors cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5" />
              Add another product line
            </button>
          </div>


          {/* ── STICKY FOOTER ACTION BAR ── */}
          <div className={`fixed bottom-0 left-0 md:left-64 right-0 z-50 p-4 border-t shadow-lg flex items-center justify-between transition-all duration-300 ${
            isDark ? 'bg-slate-900 border-slate-800/80 text-white' : 'bg-white border-gray-205 text-gray-850'
          }`}>
            <div className="flex items-center gap-5 pl-4 flex-wrap">
              {/* Total Items */}
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                  <Boxes className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-[9px] font-black uppercase tracking-wider text-gray-400 leading-none mb-1">Total Items</p>
                  <p className={`text-sm font-black leading-none ${isDark ? 'text-white' : 'text-gray-900'}`}>{totalItems}</p>
                </div>
              </div>
              <div className="h-8 w-px bg-gray-200 dark:bg-slate-800" />

              {/* Total Quantity */}
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-950/20 flex items-center justify-center text-blue-600 dark:text-blue-400">
                  <Boxes className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-[9px] font-black uppercase tracking-wider text-gray-400 leading-none mb-1">Total Quantity</p>
                  <p className={`text-sm font-black leading-none ${isDark ? 'text-white' : 'text-gray-900'}`}>{totalQty}</p>
                </div>
              </div>
              <div className="h-8 w-px bg-gray-200 dark:bg-slate-800" />

              {/* Bonus Qty */}
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 flex items-center justify-center text-emerald-600 dark:text-emerald-450">
                  <Star className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-[9px] font-black uppercase tracking-wider text-gray-400 leading-none mb-1">Bonus Qty</p>
                  <p className={`text-sm font-black leading-none ${isDark ? 'text-white' : 'text-gray-900'}`}>{totalBonus}</p>
                </div>
              </div>
              <div className="h-8 w-px bg-gray-200 dark:bg-slate-800 hidden sm:block" />

              {/* Grand Total */}
              <div className="hidden sm:flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-purple-50 dark:bg-purple-950/20 flex items-center justify-center text-purple-650 dark:text-purple-400">
                  <CircleDollarSign className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-[9px] font-black uppercase tracking-wider text-gray-400 leading-none mb-1">Grand Total</p>
                  <p className={`text-sm font-black leading-none ${isDark ? 'text-white' : 'text-gray-900'}`}>₹{grandTotal.toFixed(2)}</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 pr-4">
              <button
                onClick={handleDiscard}
                disabled={saving || savingDraft}
                className={`px-5 py-2 border rounded-xl text-xs font-bold hover:bg-gray-50 dark:hover:bg-slate-800 transition-all cursor-pointer disabled:opacity-50 ${
                  isDark ? 'border-slate-800 text-gray-400' : 'border-gray-200 text-gray-500'
                }`}
              >
                Discard
              </button>

              <button
                onClick={handleSaveDraft}
                disabled={saving || savingDraft}
                className={`flex items-center gap-1.5 px-5 py-2 border rounded-xl text-xs font-bold hover:bg-gray-50 dark:hover:bg-slate-800 transition-all cursor-pointer disabled:opacity-50 ${
                  isDark ? 'border-slate-800 text-gray-300' : 'border-gray-200 text-gray-600'
                }`}
              >
                {savingDraft && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Save as Draft
              </button>

              <button
                onClick={handleSaveStockIn}
                disabled={saving || savingDraft}
                className="flex items-center gap-1.5 px-5 py-2 rounded-xl text-xs font-bold text-white shadow-md hover:opacity-90 transition-all cursor-pointer disabled:opacity-60"
                style={{ backgroundColor: currentTheme.color }}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saving ? 'Saving...' : 'Save Stock In'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default StockIn;